import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Accrual,
  cashflowApi,
  CashflowOverview,
  CashflowPeriodParams,
  CashflowProvider,
  CreateAccrualDto,
  CreateSettlementDto,
  Settlement,
} from '../../../api/cashflow';
import { useConfirm } from '../../../components/shared/ConfirmDialog';
import {
  CashflowTab,
  PeriodPreset,
  ProviderPatch,
  UseCashflowReturn,
} from '../types';
import { getErrorMessage } from '@/lib/errors';
import { tashkentToday } from '@/lib/datetime';

/** Календарная дата `YYYY-MM-DD`; день и месяц могут выходить за границы и переносятся. */
const calendarDay = (year: number, monthIndex: number, day: number) =>
  new Date(Date.UTC(year, monthIndex, day)).toISOString().slice(0, 10);

/** Пресет → диапазон дат. `all` — без границ: считаем за всё время. */
export function periodToParams(preset: PeriodPreset): CashflowPeriodParams {
  const today = tashkentToday();
  const [year, month, day] = today.split('-').map(Number);
  const monthIndex = month - 1;
  switch (preset) {
    case '7d':
      return { from: calendarDay(year, monthIndex, day - 6), to: today };
    case '30d':
      return { from: calendarDay(year, monthIndex, day - 29), to: today };
    case 'month':
      return { from: calendarDay(year, monthIndex, 1), to: today };
    case 'prevMonth':
      return { from: calendarDay(year, monthIndex - 1, 1), to: calendarDay(year, monthIndex, 0) };
    default:
      return {};
  }
}

const emptyForm = (): CreateSettlementDto => ({
  date: tashkentToday(),
  providerId: '',
  amount: 0,
  comment: '',
});

const emptyAccrualForm = (): CreateAccrualDto => ({
  date: tashkentToday(),
  providerId: '',
  amount: 0,
  userId: null,
  comment: '',
});

/**
 * Курс, подставляемый в новую запись: последний введённый по этому провайдеру.
 * Берём из карточки сверки — там он уже посчитан для переоценки долга.
 */
const lastRateOf = (
  overview: CashflowOverview | null,
  providerId: string,
): number | undefined => {
  const row = overview?.providers.find((p) => p.providerId === providerId);
  return row && row.rate > 0 ? row.rate : undefined;
};

export function useCashflow(): UseCashflowReturn {
  const confirm = useConfirm();
  const [overview, setOverview] = useState<CashflowOverview | null>(null);
  const [providers, setProviders] = useState<CashflowProvider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [period, setPeriod] = useState<PeriodPreset>('all');
  const [isSettlementDialogOpen, setIsSettlementDialogOpen] = useState(false);
  const [isCommissionsDialogOpen, setIsCommissionsDialogOpen] = useState(false);
  const [editingSettlement, setEditingSettlement] = useState<Settlement | null>(null);
  const [formData, setFormData] = useState<CreateSettlementDto>(emptyForm());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tab, setTab] = useState<CashflowTab>('settlements');
  const [isAccrualDialogOpen, setIsAccrualDialogOpen] = useState(false);
  const [editingAccrual, setEditingAccrual] = useState<Accrual | null>(null);
  const [accrualForm, setAccrualForm] = useState<CreateAccrualDto>(emptyAccrualForm());

  const params = useMemo(() => periodToParams(period), [period]);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [data, providersList] = await Promise.all([
        cashflowApi.getOverview(params),
        cashflowApi.getProviders(),
      ]);
      setOverview(data);
      setProviders(providersList);
    } catch (error: any) {
      setLoadError(getErrorMessage(error, 'Не удалось загрузить сверку'));
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openCreate = () => {
    const providerId = providers.find((p) => p.is_active)?.id || '';
    setEditingSettlement(null);
    setFormData({
      ...emptyForm(),
      providerId,
      rate: lastRateOf(overview, providerId),
    });
    setIsSettlementDialogOpen(true);
  };

  const openEdit = (settlement: Settlement) => {
    setEditingSettlement(settlement);
    setFormData({
      date: settlement.date,
      providerId: settlement.provider_id,
      amount: settlement.amount,
      rate: settlement.rate,
      comment: settlement.comment || '',
    });
    setIsSettlementDialogOpen(true);
  };

  const validate = (): boolean => {
    if (!formData.providerId) {
      toast.error('Выберите провайдера');
      return false;
    }
    if (!formData.date) {
      toast.error('Укажите дату зачисления');
      return false;
    }
    // Ноль бессмысленен, отрицательное — допустимо: так заводится возврат.
    if (!formData.amount) {
      toast.error('Укажите сумму');
      return false;
    }
    if (!validateRate(formData.providerId, formData.rate)) return false;
    return true;
  };

  /** У валютного провайдера без курса запись не свести с суммовым итогом. */
  const validateRate = (providerId: string, rate?: number): boolean => {
    const provider = providers.find((p) => p.id === providerId);
    if (!provider || provider.currency === 'UZS') return true;
    if (!rate || rate <= 0) {
      toast.error(`Укажите курс ${provider.currency} к суму`);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (isSubmitting || !validate()) return;
    setIsSubmitting(true);
    try {
      if (editingSettlement) {
        // Из выписки: сумма, дата и провайдер пришли из банка — отправляем только комментарий
        await cashflowApi.updateSettlement(
          editingSettlement.id,
          editingSettlement.bank_fingerprint ? { comment: formData.comment } : formData,
        );
        toast.success('Поступление обновлено');
      } else {
        await cashflowApi.createSettlement(formData);
        toast.success('Поступление добавлено');
      }
      setIsSettlementDialogOpen(false);
      setEditingSettlement(null);
      fetchAll();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось сохранить поступление'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Удалить поступление?',
      description: 'Запись исчезнет из сверки, долг провайдера пересчитается.',
      confirmLabel: 'Удалить',
      destructive: true,
    });
    if (!ok) return;
    try {
      await cashflowApi.deleteSettlement(id);
      toast.success('Поступление удалено');
      fetchAll();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось удалить поступление'));
    }
  };

  const openAccrualCreate = () => {
    const providerId = providers.find((p) => p.is_active && p.source === 'manual')?.id || '';
    setEditingAccrual(null);
    setAccrualForm({
      ...emptyAccrualForm(),
      providerId,
      rate: lastRateOf(overview, providerId),
    });
    setIsAccrualDialogOpen(true);
  };

  const openAccrualEdit = (accrual: Accrual) => {
    setEditingAccrual(accrual);
    setAccrualForm({
      date: accrual.date,
      providerId: accrual.provider_id,
      amount: accrual.amount,
      rate: accrual.rate,
      userId: accrual.user_id,
      comment: accrual.comment || '',
    });
    setIsAccrualDialogOpen(true);
  };

  const handleAccrualSubmit = async () => {
    if (isSubmitting) return;
    if (!accrualForm.providerId) {
      toast.error('Выберите провайдера');
      return;
    }
    if (!accrualForm.date) {
      toast.error('Укажите дату продажи');
      return;
    }
    // Отрицательная сумма допустима: так заводится корректировка.
    if (!accrualForm.amount) {
      toast.error('Укажите сумму');
      return;
    }
    if (!validateRate(accrualForm.providerId, accrualForm.rate)) return;

    setIsSubmitting(true);
    try {
      if (editingAccrual) {
        await cashflowApi.updateAccrual(editingAccrual.id, accrualForm);
        toast.success('Начисление обновлено');
      } else {
        await cashflowApi.createAccrual(accrualForm);
        toast.success('Начисление добавлено');
      }
      setIsAccrualDialogOpen(false);
      setEditingAccrual(null);
      fetchAll();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось сохранить начисление'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAccrualDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Удалить начисление?',
      description: 'Продажа исчезнет из собранного, долг провайдера пересчитается. Премиум пользователя это не затронет.',
      confirmLabel: 'Удалить',
      destructive: true,
    });
    if (!ok) return;
    try {
      await cashflowApi.deleteAccrual(id);
      toast.success('Начисление удалено');
      fetchAll();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось удалить начисление'));
    }
  };

  const handleUpdateProvider = async (id: string, patch: ProviderPatch) => {
    try {
      await cashflowApi.updateProvider(id, patch);
      toast.success('Настройки провайдера сохранены');
      fetchAll();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось сохранить настройки'));
    }
  };

  return {
    overview,
    providers,
    isLoading,
    loadError,
    period,
    setPeriod,
    isSettlementDialogOpen,
    setIsSettlementDialogOpen,
    isCommissionsDialogOpen,
    setIsCommissionsDialogOpen,
    editingSettlement,
    formData,
    setFormData,
    isSubmitting,
    openCreate,
    openEdit,
    handleSubmit,
    handleDelete,
    tab,
    setTab,
    isAccrualDialogOpen,
    setIsAccrualDialogOpen,
    editingAccrual,
    accrualForm,
    setAccrualForm,
    openAccrualCreate,
    openAccrualEdit,
    handleAccrualSubmit,
    handleAccrualDelete,
    handleUpdateProvider,
    refresh: fetchAll,
  };
}
